import { Dialog, DialogContent } from "@nimit9/signet-ui/components/dialog"
import { bankNameOf, type ParsedCas } from "@paisa/parsers"
import type { ParsedStatement } from "@paisa/types"
import { useQuery } from "@tanstack/react-query"
import { X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { track } from "@/lib/analytics"
import { api } from "@/lib/api"
import {
  CasDetectedError,
  importEvent,
  missCopy,
  missEvent,
  PasswordRequiredError,
  parseCasFile,
  parseStatementFile,
  UnsupportedFormatError,
} from "@/lib/parse-statement"
import { findMatchingAccount } from "./-vault/helpers"
import { StepCas } from "./-vault/step-cas"
import { StepDrop, type StepDropMiss } from "./-vault/step-drop"
import { StepParsing } from "./-vault/step-parsing"
import { StepPassword } from "./-vault/step-password"
import { StepPreview } from "./-vault/step-preview"
import { StepReview } from "./-vault/step-review"
import type { AccountOption, VaultStep } from "./-vault/types"
import { VaultStepper } from "./-vault/vault-stepper"

interface UploadStatementDialogProps {
  open: boolean
  onClose: () => void
  /** A file picked or dropped elsewhere; parsed as soon as the dialog opens. */
  initialFile?: File | null
  /** "Add transactions by hand" on the miss screen: the dialog closes, then this opens manual add. */
  onManualAdd?: () => void
}

/** "Ask for my bank": the same anonymous request as the phone's (kind and name only, no session). */
async function askForBank(name: string): Promise<boolean> {
  try {
    const res = await fetch("/api/import-requests", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind: "bank", name }),
      credentials: "omit",
    })
    if (res.ok) track("import_request", { kind: "bank" })
    return res.ok
  } catch {
    return false
  }
}

export function UploadStatementDialog({
  open,
  onClose,
  initialFile,
  onManualAdd,
}: UploadStatementDialogProps) {
  const accountsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: () => api.get<{ success: boolean; data: AccountOption[] }>("/accounts"),
    enabled: open,
  })
  const accounts = accountsQuery.data?.data ?? []

  const [step, setStep] = useState<VaultStep>("drop")
  const [file, setFile] = useState<File | null>(null)
  const [fileBuffer, setFileBuffer] = useState<ArrayBuffer | null>(null)
  const [password, setPassword] = useState("")
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [parsed, setParsed] = useState<ParsedStatement | null>(null)
  const [cas, setCas] = useState<ParsedCas | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [miss, setMiss] = useState<StepDropMiss | null>(null)
  const [asked, setAsked] = useState<"idle" | "sending" | "sent" | "failed">("idle")
  const [isParsing, setIsParsing] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)
  const [isAutoMatched, setIsAutoMatched] = useState(false)

  function reset() {
    setStep("drop")
    setFile(null)
    setFileBuffer(null)
    setPassword("")
    setPasswordError(null)
    setParsed(null)
    setCas(null)
    setParseError(null)
    setMiss(null)
    setAsked("idle")
    setIsParsing(false)
    setDragOver(false)
    setSelectedAccountId(null)
    setIsAutoMatched(false)
  }

  function handleClose() {
    reset()
    onClose()
  }

  async function tryParse(buffer: ArrayBuffer, pwd?: string) {
    setIsParsing(true)
    setParseError(null)
    setMiss(null)
    setAsked("idle")
    setPasswordError(null)
    try {
      const result = await parseStatementFile(buffer, pwd)
      setParsed(result)
      const match = findMatchingAccount(accounts, result)
      setSelectedAccountId(match?.id ?? null)
      setIsAutoMatched(!!match)
      setStep("preview")
    } catch (err) {
      if (err instanceof CasDetectedError) {
        // Holdings, not transactions: a separate preview (UAT: CAS on web).
        setCas(await parseCasFile(buffer, pwd))
        setStep("cas")
      } else if (err instanceof PasswordRequiredError) {
        setStep("password")
        if (err.kind === "incorrect") setPasswordError("Incorrect password — try again")
      } else if (err instanceof UnsupportedFormatError) {
        // A miss counts here (with its bank and reason); a read counts when it is imported.
        track("import_statement", missEvent(err))
        const copy = missCopy(err)
        if (copy) setMiss({ ...copy, bank: err.bank ? (bankNameOf(err.bank) ?? null) : null })
        else
          setParseError(
            "We couldn't recognise this file. We support HDFC, SBI, and Amex today. " +
              "For other banks, use Manual CSV Import.",
          )
      } else {
        setParseError(err instanceof Error ? err.message : "Failed to parse file")
      }
    } finally {
      setIsParsing(false)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: run once per handed-in file
  useEffect(() => {
    if (open && initialFile) void onFileSelected(initialFile)
  }, [open, initialFile])

  async function onFileSelected(f: File) {
    setFile(f)
    setParseError(null)
    const buffer = await f.arrayBuffer()
    setFileBuffer(buffer)
    await tryParse(buffer)
  }

  async function onPasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!fileBuffer || !password) return
    await tryParse(fileBuffer, password)
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) onFileSelected(f)
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (f) onFileSelected(f)
  }

  // When accounts list refreshes after user adds one, auto-select if now matching
  const prevAccountsLen = useRef(accounts.length)
  useEffect(() => {
    if (!parsed || selectedAccountId) return
    if (accounts.length === prevAccountsLen.current) return
    prevAccountsLen.current = accounts.length
    const match = findMatchingAccount(accounts, parsed)
    if (match) {
      setSelectedAccountId(match.id)
      setIsAutoMatched(true)
    }
  }, [accounts, parsed, selectedAccountId])

  const matchedAccount = selectedAccountId ? accounts.find((a) => a.id === selectedAccountId) : null

  const stepperCurrent =
    step === "drop" || step === "password"
      ? "upload"
      : step === "preview" || step === "cas"
        ? "preview"
        : "review"

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="flex flex-col gap-0 overflow-hidden p-0
          max-sm:inset-0 max-sm:top-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:max-w-full max-sm:max-h-full max-sm:rounded-none
          sm:max-h-[90vh] sm:max-w-[600px]"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border-subtle px-6 py-4">
          <span className="text-[14px] font-semibold text-text-primary">
            {step === "review" && parsed
              ? `Import · ${parsed.bank.toUpperCase()}`
              : "Import statement"}
          </span>
          <button
            type="button"
            onClick={handleClose}
            className="flex size-7 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-bg-elevated hover:text-text-primary"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Stepper */}
        {step !== "password" && <VaultStepper current={stepperCurrent} />}

        {/* Step content */}
        {step === "drop" && isParsing && file ? (
          <StepParsing fileName={file.name} onCancel={handleClose} />
        ) : step === "drop" ? (
          <StepDrop
            dragOver={dragOver}
            parseError={parseError}
            miss={miss}
            asked={asked}
            onAskBank={async () => {
              if (!miss?.bank) return
              setAsked("sending")
              setAsked((await askForBank(miss.bank)) ? "sent" : "failed")
            }}
            onManualAdd={
              onManualAdd
                ? () => {
                    handleClose()
                    onManualAdd()
                  }
                : undefined
            }
            onDrop={onDrop}
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onFileChange={onFileChange}
            onCancel={handleClose}
          />
        ) : step === "password" ? (
          <StepPassword
            fileName={file?.name ?? "statement.pdf"}
            password={password}
            passwordError={passwordError}
            isParsing={isParsing}
            onChange={setPassword}
            onSubmit={onPasswordSubmit}
            onCancel={reset}
          />
        ) : step === "preview" && parsed ? (
          <StepPreview
            parsed={parsed}
            accounts={accounts}
            selectedAccountId={selectedAccountId}
            isAutoMatched={isAutoMatched}
            onSelectAccount={(id) => {
              setSelectedAccountId(id)
              setIsAutoMatched(false)
            }}
            onContinue={() => setStep("review")}
            onBack={reset}
          />
        ) : step === "cas" && cas ? (
          <StepCas cas={cas} onBack={reset} onDone={handleClose} />
        ) : step === "review" && parsed && selectedAccountId && matchedAccount ? (
          <StepReview
            parsed={parsed}
            accountId={selectedAccountId}
            accountType={matchedAccount.type}
            onBack={() => setStep("preview")}
            onDone={handleClose}
            onImportAnother={reset}
            onImported={(n) => track("import_statement", importEvent(parsed, n))}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
